import { LoadingButton } from "@mui/lab";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { setCompetitorAnalysis } from "../../../../../../redux/slices/competitorSlice";
import {
  useGetMoreKeywordsByAnalysisIdMutation,
  useReinitializeKeywordsByAnalysisIdMutation,
} from "../../../../../../redux/slices/postEditorBlogSlice";
import { dispatch } from "../../../../../../redux/store";
import { getText, removeAccents } from "../../../../seoAnalyzer/helper";
import { keywordCount } from "../../../../seoAnalyzer/semanticHelper";
import { useEditorContext } from "../../EditorContext";

export const SemanticsCompetitorAnalysis = ({
  competitorResults,
  showModal,
  showModalHandle,
  formikProps,
}) => {
  const { t } = useTranslation();
  const [keywords, setKeywords] = useState([]);
  const editor = useEditorContext();
  const wordCount = editor?.getText().trim()
    ? editor
        ?.getText()
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0).length
    : 0;

  const handleCountKeyword = (keyword) => {
    const text =
      getText(formikProps.values.introduction.trim()).toLowerCase().trim() +
      getText(formikProps.values.content.trim()).toLowerCase().trim() +
      getText(formikProps.values.conclusion.trim()).toLowerCase().trim();
    return keywordCount(removeAccents(keyword), removeAccents(text));
  };

  const handleHighlightWords = () => {
    if (editor && keywords) {
      const sortedKeywords = [...keywords].sort(
        (a, b) => b.keywords.length - a.keywords.length
      );
      editor.commands.toggleHighlighting(true);
      editor.commands.addKeywords(sortedKeywords.map((item) => item.keywords));
      var colors = sortedKeywords?.map((item) => {
        const count = handleCountKeyword(item.keywords);
        return count === 0
          ? "bg-[#D6D3D1]"
          : count < Math.round((wordCount * item.low) / 100)
          ? "bg-cyan-900/50"
          : count > Math.round((wordCount * item.high) / 100)
          ? "bg-red-700/50"
          : "bg-lime-700/50";
      });
      editor.commands.addColors(colors);
      editor.commands.highlightKeywords();
    }
  };

  const handleKeyword = (item) => {
    setKeywords((prevList) => {
      const exists = prevList.some((i) => i.keywords === item.keywords);
      if (exists) {
        // Remove item if it exists
        return prevList.filter((i) => i.keywords !== item.keywords);
      } else {
        // Add item if it doesn't exist
        return [...prevList, item];
      }
    });
  };

  useEffect(() => {
    handleHighlightWords();
  }, [keywords, formikProps.values]);

  const handleShowModal = () => {
    showModalHandle({
      keywords: competitorResults?.keywords_analysis,
      show: true,
      type: "semantics",
      semantics_view: competitorResults?.semantics_view,
    });
  };

  const [loadKeywords, { isLoading: keywordLoading }] =
    useGetMoreKeywordsByAnalysisIdMutation();
  const [reinitalizeKeywords, { isLoading: reinitalizeKeywordsLoading }] =
    useReinitializeKeywordsByAnalysisIdMutation();

  const handleShowMoreKeywords = async (id, limit) => {
    const res = await loadKeywords({
      seo_analysis_id: id,
      limit: limit,
    }).unwrap();

    if (res?.nb_keywords) {
      dispatch(
        setCompetitorAnalysis({
          data: {
            ...competitorResults,
            keywords: [...competitorResults?.keywords, ...res.keywords],
            nb_keywords: res?.nb_keywords,
          },
        })
      );
    }
  };

  const handleReinitializeKeywords = async (id) => {
    const res = await reinitalizeKeywords({ seo_analysis_id: id }).unwrap();
    if (res?.nb_keywords) {
      dispatch(
        setCompetitorAnalysis({
          data: {
            ...competitorResults,
            keywords: res.keywords,
            nb_keywords: res?.nb_keywords,
          },
        })
      );
    }
  };

  return (
    <div className="mb-8">
      {/* Competitor Analysis */}
      {competitorResults &&
        competitorResults?.keywords_analysis?.secondary_keywords?.length >
          0 && (
          <>
            {/* {Secondary Keywords} */}
            <p className="text-white text-xl mt-6 mb-4 font-bold">
              {t("Secondary-keywords", "Secondary Keywords")}{" "}
            </p>

            <div className="flex gap-3 items-center flex-wrap text-white">
              {competitorResults?.keywords_analysis?.secondary_keywords?.map(
                (item) => {
                  return (
                    <div
                      onClick={() => {
                        handleKeyword(item);
                      }}
                      className={` rounded font-bold px-3 py-2 text-xs text-black cursor-pointer drop-shadow-xl
                      ${
                        handleCountKeyword(item.keywords) == 0
                          ? `${
                              keywords.some((i) => i.keywords === item.keywords)
                                ? "bg-[#b1b1b1]"
                                : "bg-[#D6D3D1]"
                            }`
                          : handleCountKeyword(item.keywords) <
                            Math.round((wordCount * item.low) / 100)
                          ? `${
                              keywords.some((i) => i.keywords === item.keywords)
                                ? "bg-[#598495]"
                                : "bg-[#8AA6B1]"
                            }`
                          : handleCountKeyword(item.keywords) >
                            Math.round((wordCount * item.high) / 100)
                          ? `${
                              keywords.some((i) => i.keywords === item.keywords)
                                ? "bg-[#ad7c7a]"
                                : "bg-[#DF9290]"
                            }`
                          : `${
                              keywords.some((i) => i.keywords === item.keywords)
                                ? "bg-[#849966]"
                                : "bg-[#A6BD87]"
                            }`
                      } 
                                        `}
                    >
                      {" "}
                      {item.keywords}{" "}
                    </div>
                  );
                }
              )}
            </div>

            {/* Load More Keywords */}
            {!showModal && (
              <div className="text-center mt-5">
                <button
                  sx={{
                    "&.Mui-disabled": {
                      backgroundColor: "gainsboro",
                      color: "transparent",
                    },
                  }}
                  className="bg-blue-200  hover:bg-blue-100  px-5 py-[6px] rounded  text-sm text-white"
                  onClick={handleShowModal}
                >
                  {t("See-the-full-repartition", "See the full repartition")}
                </button>
              </div>
            )}
          </>
        )}

      {/* Additional Keywords */}
      {competitorResults && competitorResults?.keywords?.length > 0 && (
        <>
          <p className="text-white text-xl mt-6 mb-4 font-bold">
            {t("Additional-keywords", "Additional Keywords")}{" "}
          </p>

          <div className="flex gap-3 items-center flex-wrap ">
            {competitorResults?.keywords?.map((item) => {
              return (
                <>
                  <div
                    className={`${
                      handleCountKeyword(item) > 0
                        ? "bg-green-200"
                        : "bg-stone-300 "
                    } rounded font-bold px-3 py-2 text-xs text-black`}
                  >
                    {item}
                  </div>
                </>
              );
            })}
          </div>
          <div className="text-center mt-5 flex items-center justify-center gap-2 text-white">
            {competitorResults?.has_more_keywords &&
            competitorResults?.has_more_keywords ? (
              <LoadingButton
                variant="contained"
                loading={keywordLoading}
                className="!text-sm hover:!bg-blue-100 *: !bg-blue-200 disabled:!bg-slate-100 disabled:!text-transparent
                                                  !text-white font-light px-5 py-[6px] rounded flex items-center !normal-case"
                onClick={() => {
                  handleShowMoreKeywords(
                    competitorResults?.id,
                    competitorResults?.nb_keywords
                  );
                }}
              >
                {t("See-more-keywords", "See more keywords")}
              </LoadingButton>
            ) : null}
            {competitorResults?.keywords?.length > 20 ? (
              <LoadingButton
                variant="contained"
                loading={reinitalizeKeywordsLoading}
                sx={{
                  "&.Mui-disabled": {
                    backgroundColor: "gainsboro",
                    color: "transparent",
                  },
                }}
                className="!text-sm !bg-slate-100 hover:!bg-blue-100 hover:!text-white disabled:!text-transparent
                                          !text-black font-light px-5 py-[6px] rounded flex items-center !capitalize"
                onClick={() => {
                  handleReinitializeKeywords(competitorResults?.id);
                }}
              >
                {t("Re-initalize", "Re-initalize")}
              </LoadingButton>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
};
